import React, { useState } from "react";
import Divider from "@mui/material/Divider";
import { IconButton } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { IoMdClose } from "react-icons/io";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { submitFeedback as submitFeedbackRequest } from "../../../../services/apiServices";
import { ACTIVE_CONVERSATION_QUERY_KEY } from "../../../../hooks/chat/constants";

const voteToRating = (vote) => (vote === "no" ? "negative" : "positive");
const ratingToVote = (rating) => (rating === "negative" ? "no" : "yes");

// Comment-prefill suggestions for positive feedback (free-text helpers).
const POSITIVE_SUGGESTIONS = [
  "This is exactly what I was looking for—clear and concise!",
  "Great response!",
];

// Single-select structured reasons for negative feedback. Values match the
// backend `Feedback.REASON_CHOICES`.
const NEGATIVE_REASONS = [
  { value: "incorrect_data", label: "Incorrect data" },
  { value: "missing_information", label: "Missing information" },
  { value: "hallucinated_answer", label: "Hallucinated answer" },
  { value: "outdated_portfolio_info", label: "Outdated portfolio info" },
  { value: "wrong_calculation", label: "Wrong calculation" },
  { value: "poor_formatting", label: "Poor formatting" },
  { value: "other", label: "Other" },
];

/**
 * Yes/No accuracy prompt + reason/comment form rendered after every
 * non-thinking assistant reply (skipped on shared-chat views and on the
 * first message in the conversation).
 *
 * Negative feedback offers a single-select reason from a fixed catalogue
 * (stored structurally on the feedback record); positive feedback keeps the
 * free-text comment suggestions. Submission posts to the dedicated feedback
 * endpoint, which persists the record and notifies the business team on Slack.
 */
const MessageFeedback = ({ message, clientId, conversationId }) => {
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const queryClient = useQueryClient();

  // Feedback already given on this message (rehydrated from the conversation
  // load payload) seeds the UI so the vote/reason/comment/thank-you persist
  // across reloads instead of resetting.
  const persisted = message.feedback; // { rating, reason, comment } | null

  const [feedback, setFeedback] = useState(
    persisted ? ratingToVote(persisted.rating) : "",
  );
  const [reason, setReason] = useState(persisted?.reason ?? null);
  const [textContent, setTextContent] = useState(persisted?.comment ?? "");
  const [submitted, setSubmitted] = useState(Boolean(persisted));
  const [showComment, setShowComment] = useState(Boolean(persisted));

  const isNegative = feedback === "no";

  const {
    mutateAsync: submitFeedback,
    isPending,
    isError,
  } = useMutation({
    mutationFn: () =>
      submitFeedbackRequest(clientId, {
        conversationId,
        messageId: message.id,
        rating: voteToRating(feedback),
        // Reason only applies to negative feedback.
        reason: isNegative ? reason : null,
        comment: textContent,
      }),
    onSuccess: () => {
      setSubmitted(true);
      // Mirror the new feedback onto the cached conversation message so an
      // in-session remount (e.g. navigating away and back without a server
      // refetch) keeps the state — same pattern as the message-id stamping.
      queryClient.setQueryData(
        [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
        (prev) => {
          if (!prev) return prev;
          const idx = prev.messages.findIndex((m) => m.id === message.id);
          if (idx === -1) return prev;
          const next = prev.messages.slice();
          next[idx] = {
            ...next[idx],
            feedback: {
              rating: voteToRating(feedback),
              reason: isNegative ? reason : null,
              comment: textContent,
            },
          };
          return { ...prev, messages: next };
        },
      );
    },
  });

  const handleVote = (vote) => {
    setShowComment(true);
    setFeedback(vote);
    // Reason is meaningless for a positive vote; clear it when switching.
    if (vote === "yes") setReason(null);
    // Changing the vote re-opens the form so the user can revise and resend.
    setSubmitted(false);
  };

  return (
    <>
      <Divider
        sx={{ margin: "1rem 0", width: isSmallScreen ? "100%" : "60%" }}
      />
      <div className="feedback--wrapper">
        <p>Was this answer accurate?</p>
        <div style={{ display: "flex", gap: "20px" }}>
          <button
            className={`feedback-btn ${feedback === "no" ? "highlighted" : ""}`}
            onClick={() => handleVote("no")}
          >
            No
          </button>
          <button
            className={`feedback-btn ${feedback === "yes" ? "highlighted" : ""}`}
            onClick={() => handleVote("yes")}
          >
            Yes
          </button>
        </div>
      </div>
      {showComment &&
        (submitted ? (
          <div className="feedback-successMsg--container">
            <div className="feedback-successMsg--wrapper">
              <h4>Thank you for the feedback!</h4>
              <button
                type="button"
                className="feedback-change-btn"
                onClick={() => setSubmitted(false)}
              >
                Change
              </button>
            </div>
            <div>
              <IconButton
                color="primary"
                onClick={() => setShowComment(false)}
              >
                <IoMdClose />
              </IconButton>
            </div>
          </div>
        ) : (
          <div className="feedback-comment--container">
            <div className="feedback-comment-header--wrapper">
              <h4>{isNegative ? "What went wrong?" : "Tell us more"}</h4>
              <div>
                <IconButton
                  color="primary"
                  onClick={() => setShowComment(false)}
                >
                  <IoMdClose />
                </IconButton>
              </div>
            </div>
            {isNegative ? (
              <div className="feedback-reasons--container">
                {NEGATIVE_REASONS.map(({ value, label }) => (
                  <button
                    type="button"
                    key={value}
                    className={`feedback-reason--chip ${
                      reason === value ? "selected" : ""
                    }`}
                    aria-pressed={reason === value}
                    onClick={() =>
                      setReason((prev) => (prev === value ? null : value))
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="feedback-suggestions--container">
                {POSITIVE_SUGGESTIONS.map((suggestion, i) => (
                  <div
                    key={`feedback-suggestion-${i}`}
                    className="feedback-suggection--wrapper"
                    onClick={() => setTextContent(suggestion)}
                  >
                    <p>{suggestion}</p>
                  </div>
                ))}
              </div>
            )}
            <form
              className="feedback-comment-form"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await submitFeedback();
                } catch (_err) {
                  // Error surfaced below via isError; mutateAsync rejects on
                  // failure and we don't want an unhandled rejection.
                }
              }}
            >
              <textarea
                placeholder={
                  isNegative
                    ? "Add any details (optional)"
                    : "Send us a message telling why the answer is accurate"
                }
                rows="4"
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
              ></textarea>
              {isError && (
                <p className="feedback-error">
                  Something went wrong. Please try again.
                </p>
              )}
              <button type="submit" disabled={isPending}>
                {isPending ? "sending…" : "send"}
              </button>
            </form>
          </div>
        ))}
    </>
  );
};

export default MessageFeedback;
